import { Alert, Button, Input, Switch, Typography } from "antd";
import { CloseOutlined, SaveOutlined } from "@ant-design/icons";
import { ToastChitFlags, useGDA } from "context/GDAContext";
import { useCallback, useEffect, useMemo, useState } from "react";

import styled from "styled-components";

const ToastChitContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  h5 {
    margin: 1rem 0 0;
  }

  button {
    align-self: flex-start;
    margin-top: 8px;
  }
`;

const ToastChitRow = styled.div`
  display: flex;
  align-items: left;
  gap: 16px;
`;

const SwitchContainer = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px 0 8px 0;
`;

const ToggleItem = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;

  button {
    margin-top: 0;
  }
`;

const ToggleSection = styled.div`
  display: flex;
  flex-direction: column;
`;

interface ToggleConfig {
  key: keyof ToastChitFlags;
  label: string;
  testId: string;
}

const TOAST_CHIT_TOGGLES: ToggleConfig[] = [
  { key: "guestNoteEnabled", label: "Guest note", testId: "guest-note-switch" },
  { key: "reservationNoteEnabled", label: "Reservation / visit note", testId: "reservation-visit-note-switch" },
  { key: "allergiesEnabled", label: "Allergies", testId: "allergies-switch" },
  { key: "specialOccasionEnabled", label: "Special Occasion", testId: "special-occasion-switch" },
];

function ToastChits() {
  const [text, setText] = useState<string>("");
  const [flags, setFlags] = useState<ToastChitFlags>({
    guestNoteEnabled: false,
    reservationNoteEnabled: false,
    allergiesEnabled: false,
    specialOccasionEnabled: false,
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    isGDAEnabled,
    toastChits,
    handleUpdateToastChits,
    isToastChitsLoading,
    toastChitsFlags,
  } = useGDA();

  const { TextArea } = Input;

  useEffect(() => {
    setText(toastChits ?? "");
    setFlags(toastChitsFlags);
  }, [toastChits, toastChitsFlags]);

  const hasChanges = useMemo(() => {
    const hasTextChanges = text !== (toastChits ?? "");

    const hasFlagChanges = TOAST_CHIT_TOGGLES.some(
      ({ key }) => flags[key] !== toastChitsFlags[key],
    );

    return hasTextChanges || hasFlagChanges;
  }, [flags, text, toastChits, toastChitsFlags]);

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = event.target.value;
      setText(value);
    },
    [],
  );

  const handleSave = useCallback(() => {
    if (!handleUpdateToastChits || !hasChanges) {
      return;
    }

    setIsLoading(true);
    handleUpdateToastChits({ note: text, flags })
      .then(() => {
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, [flags, handleUpdateToastChits, hasChanges, text]);

  const handleCancel = useCallback(() => {
    setText(toastChits ?? "");
    setFlags(toastChitsFlags);
  }, [toastChits, toastChitsFlags]);

  const handleFlagChange = useCallback(
    (key: keyof ToastChitFlags, checked: boolean) => {
      setFlags((previousFlags) => ({
        ...previousFlags,
        [key]: checked,
      }));
    },
    [],
  );

  return (
    <ToastChitContainer>
      <ToastChitRow style={{ justifyContent: "flex-end" }}>
        <Button
          disabled={!hasChanges || isToastChitsLoading}
          loading={isLoading ?? isToastChitsLoading}
          onClick={handleSave}
          type="primary"
          icon={<SaveOutlined />}
        >
          Save Changes
        </Button>
        <Button
          disabled={!hasChanges}
          onClick={handleCancel}
          type="default"
          icon={<CloseOutlined />}
        >
          Cancel
        </Button>
      </ToastChitRow>
      <ToggleSection>
        <Typography.Title level={5}>Toast Chit note types:</Typography.Title>
        <SwitchContainer>
          {TOAST_CHIT_TOGGLES.map(({ key, label, testId }) => (
            <ToggleItem key={key}>
              <Switch
                checked={flags[key]}
                onChange={(checked) => handleFlagChange(key, checked)}
                data-testid={testId}
              />
              <Typography.Text>{label}</Typography.Text>
            </ToggleItem>
          ))}
        </SwitchContainer>
      </ToggleSection>
      <Typography.Title level={5}>GDA Toast Chit note:</Typography.Title>
      <TextArea
        showCount
        maxLength={75}
        value={text}
        onChange={onChange}
        placeholder="Enter Toast Chit message"
        style={{ height: 60, resize: "none" }}
      />
      {!isGDAEnabled && (
        <ToastChitRow>
          <Alert
            type="warning"
            message={
              <>
                <strong>Note: </strong>
                <span>
                  GDA Toast Chit notes will only be sent to the POS when the GDA
                  location (top right) is enabled.
                </span>
              </>
            }
            style={{
              marginTop: "24px",
              width: "100%"
            }}
          />
        </ToastChitRow>
      )}
    </ToastChitContainer>
  );
}

export default ToastChits;
